import { HttpsAgent } from "agentkeepalive"
import axios, { AxiosRequestConfig } from "axios"
import axiosRetry, { IAxiosRetryConfig } from "axios-retry"

export interface commonAxiosResponse<T = any> {
  success: boolean
  message?: string
  data?: T
}

const isBrowser = typeof window !== "undefined"

export const defaultHttpsAgent = new HttpsAgent({ keepAlive: true })
export const defaultTimeout = 40000

// In-memory token storage — intentionally cleared on page reload
let _accessToken: string | null = null
const _accountTokens: Map<string, string> = new Map()

export const getAccessToken = (): string | null => _accessToken
export const setAccessToken = (token: string | null): void => {
  _accessToken = token
}
export const getAccountToken = (accountId: string): string | null =>
  _accountTokens.get(accountId) ?? null
export const setAccountTokens = (accounts: Array<{ id: string; authToken: string }>): void => {
  accounts.forEach(({ id, authToken }) => _accountTokens.set(id, authToken))
}

export const defaultConfig: AxiosRequestConfig = {
  timeout: defaultTimeout,
  ...(isBrowser ? {} : { httpsAgent: defaultHttpsAgent }),
  headers: {
    app: "Gunsens Admin Panel",
  },
}

console.log("API Base URL:", process.env.REACT_APP_API_BASE_URL)

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "",
  ...defaultConfig,
})

axiosRetry(apiClient, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: any) => {
    return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.response?.status >= 500
  },
} as IAxiosRetryConfig)

apiClient.interceptors.request.use((config) => {
  // Refresh endpoint authenticates via cookie only — never send the (possibly expired) access token
  if (config.url === "/auth/refresh") return config
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = []

const processQueue = (error: any, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token!)
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config

    // Skip refresh loop for the refresh endpoint itself or already-retried requests
    if (
      err.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== "/auth/refresh"
    ) {
      if (isRefreshing) {
        // Queue this request until the in-flight refresh completes
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return apiClient(originalRequest)
          })
          .catch((queueErr) => Promise.reject(queueErr))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const res = await apiClient.post("/auth/refresh", undefined, { withCredentials: true })
        const responseData = res.data?.data || {}
        const { authToken, accounts } = responseData
        setAccessToken(authToken)
        if (accounts?.length) setAccountTokens(accounts)
        processQueue(null, authToken)
        originalRequest.headers.Authorization = `Bearer ${authToken}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        window.location.href = "/login"
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(err)
  },
)

export default apiClient
